import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { p, tp, useP } from '../i18n/useP';
import {
  getBsView, patchBsAccountLine, importBsExcel, setBsMonthly, setBsLineForecast,
  createBsLine, updateBsLine, deleteBsLine, reorderBsLines, renameBsSection, setBsNote,
} from '../api/financials_bs';
import { usePersistentSet, usePersistentValue } from '../hooks/usePersistentState';
import {
  getPlView, listLines as listPlLines,
  createAdjustment, createAdjustmentLine,
} from '../api/financials_pl';
import PageHeader from '../components/PageHeader';
import CardShell from '../components/CardShell';
import PillFilter from '../components/PillFilter';
import '../components/EditableCell.css';
import './BalanceSheetPage.css';

const MONTHS = ['Jan', tp('phrases:p_7b8852ec47'), 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', tp('phrases:p_39184c9361'), 'Sep', 'Oct', 'Nov', tp('phrases:p_2bc2249a06')];

// Niveau de lecture (bouton cyclique) : 1 synthétique · 2 ouvert (lignes) ·
// 3 tout ouvert (comptes). L'icône suggère le degré de dépliage.
const LEVEL_LABELS = { 1: p('⊟ Synthétique'), 2: p('⊡ Ouvert'), 3: p('⊞ Tout ouvert') };

/* RACCOURCIS PAR MASSE DU BILAN (demande d'Olivier, 2026-10-03) : une
   pastille par agrégat, Fonds étrangers réunissant les dettes à court et à
   long terme. Une pastille retenue ne montre que ses masses, TOUT OUVERT
   (lignes et comptes) ; aucune retenue rend la lecture choisie au bouton de
   niveau, qui n'est pas touchée. */
export const BS_AGGREGATES = [
  { value: 'current_assets', label: p('Actifs circulants'), sections: ['current_assets'] },
  { value: 'fixed_assets', label: p('Actifs immobilisés'), sections: ['fixed_assets'] },
  { value: 'foreign_funds', label: p('Fonds étrangers'), sections: ['current_liabilities', 'long_term_liabilities'] },
  { value: 'equity', label: p('Fonds propres'), sections: ['equity'] },
];
export function aggregateSections(picked) {
  const out = new Set();
  BS_AGGREGATES.forEach((a) => { if (picked.has(a.value)) a.sections.forEach((sc) => out.add(sc)); });
  return out;
}

// Par défaut en kCHF (milliers). thousands=false → au franc près (unité).
function chf(v, thousands = true) {
  const n = Number(v || 0);
  if (n === 0) return '–';
  return Math.round(thousands ? n / 1000 : n).toLocaleString('de-CH');
}

// ── Icônes inline (réel seul) — pas de dépendance externe, SVG stroke ─────────
const IconChevron = ({ open }) => (
  <svg className={`bs-chev${open ? ' is-open' : ''}`} viewBox="0 0 24 24" width="12" height="12" aria-hidden>
    <polyline points="9 6 15 12 9 18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
    <polyline points="8.5 12.2 11 14.7 15.6 9.3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconAlert = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
    <path d="M12 4 21 19H3z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M12 10v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="currentColor" />
  </svg>
);

/* `readOnly` — RESTITUTION PURE : aucune écriture possible (import, saisie,
   ajustement, note, glisser-déposer, structure). Distinct de `actualsOnly`, qui
   ne fait que borner l'affichage aux mois clôturés. La saisie du réel vit dans
   le hub Données, la structure dans Paramètres. */
/* `flatForecast` — lit le bilan hybride (réel ≤ clôture, prévision au-delà,
   Liquidité en plug), la MÊME source que le hub Prévisionnel : les deux vues ne
   peuvent donc pas diverger. Combiné à `actualsOnly=false`, c'est la vue
   « Prévisionnel » des Rapports. */
export default function BalanceSheetPage({ activeTenant, addToast, year, onYearChange, embedded, actualsOnly = false, readOnly = false, flatForecast = false, monthsFull = false, version = null, unit, toolsHost = null, cutoff = null, loadedMonths = false }) {
  const { t } = useTranslation();
  const p = useP();
  const [view, setView]                 = useState(null);
  const [plNetResult, setPlNetResult]   = useState(null);
  const [plPrevNet, setPlPrevNet]         = useState(null);   // résultat net de l'exercice N-1 (contrôle de décembre)
  const [loading, setLoading]           = useState(false);
  const [importing, setImporting]       = useState(false);
  const [showStructure, setShowStruct]  = useState(false);
  const [brushMode, setBrushMode]       = useState(false);
  const [activeLine, setActiveLine]     = useState(null);
  // Verrou maître : « Déverrouiller » révèle le groupe d'outils (Édition · Ajuster ·
  // Brush · Structure · Notes) ; verrouillé, ils sont masqués.
  const [unlocked, setUnlocked]         = useState(false);
  const bsKey = `bs_${activeTenant?.id}_${year}`;
  const [expanded, setExpanded]         = usePersistentSet(`${bsKey}_exp`);
  const [expandedSecs, setExpandedSecs] = usePersistentSet(`${bsKey}_secs`);
  const [level, setLevel]               = usePersistentValue(`bs_level_${activeTenant?.id}`, 1);
  const [aggs, setAggs]                 = useState(() => new Set());
  const [thousandsLocal, setThousands]  = usePersistentValue(`bs_unit_${activeTenant?.id}`, true);
  // Unité contrôlée par le parent (hub États financiers) si `unit` est fourni ;
  // sinon bascule locale (usage autonome : bilan).
  const unitControlled = unit !== undefined;
  const thousands = unitControlled ? unit !== 'chf' : thousandsLocal;
  const [editMode, setEditMode]         = useState(false);
  const [adjustMode, setAdjustMode]     = useState(false);
  const [adjustTarget, setAdjustTarget] = useState(null);   // { lineId, label, month }
  const [notesMode, setNotesMode]       = useState(false);
  const [plLines, setPlLines]           = useState([]);
  const fileRef = useRef();

  async function handleEditValue(accountNumber, month, value) {
    const res = await setBsMonthly(activeTenant.id, { year, account_number: accountNumber, month, amount: value });
    if (res.data) reload(true);
    else addToast(res.error || p('Erreur mise à jour'), 'error');
  }

  async function handleEditForecast(lineId, month, value) {
    const res = await setBsLineForecast(activeTenant.id, { year, reporting_line_id: lineId, month, amount: value });
    if (res.data) reload(true);
    else addToast(res.error || p('Erreur forecast'), 'error');
  }

  async function handleEditNote(accountNumber, month, current) {
    const next = window.prompt(`Note — compte ${accountNumber}, ${MONTHS[month - 1]} ${year} :`, current || '');
    if (next == null) return;                       // annulé
    if (next.trim() === (current || '').trim()) return;
    const res = await setBsNote(activeTenant.id, { year, account_number: accountNumber, month, note: next });
    if (res.data) reload(true);
    else addToast(res.error || p('Erreur note'), 'error');
  }

  function toggleLine(code) {
    setExpanded(prev => { const n = new Set(prev); n.has(code) ? n.delete(code) : n.add(code); return n; });
  }
  function toggleSection(code) {
    setExpandedSecs(prev => { const n = new Set(prev); n.has(code) ? n.delete(code) : n.add(code); return n; });
  }

  function applyLevel(n) {
    setLevel(n);
    const allSecs  = (view?.sections || []).map(s => s.code);
    const allLines = (view?.lines    || []).map(l => l.id);
    if (n === 1)      { setExpandedSecs(new Set());         setExpanded(new Set()); }
    else if (n === 2) { setExpandedSecs(new Set(allSecs));  setExpanded(new Set()); }
    else              { setExpandedSecs(new Set(allSecs));  setExpanded(new Set(allLines)); }
  }

  const lines    = view?.lines    || [];
  const sections = view?.sections || [];

  function reload(silent = false) {
    if (!activeTenant) { setView(null); setPlNetResult(null); return; }
    if (!silent) setLoading(true);
    Promise.all([
      // `cutoff` : arrêté d'affichage (Rapports, vue Réel) — le serveur borne les vues.
      getBsView(activeTenant.id, year, { ...(flatForecast ? { flatForecast: true } : {}), ...(version ? { version } : {}), ...(cutoff ? { through: cutoff } : {}), ...(loadedMonths ? { loaded: true } : {}) }),
      getPlView(activeTenant.id, year, { ...(cutoff ? { through: cutoff } : {}), ...(loadedMonths ? { loaded: true } : {}) }),
      listPlLines(activeTenant.id),
      getPlView(activeTenant.id, year - 1, loadedMonths ? { loaded: true } : {}),
    ])
      .then(([bs, pl, plL, plPrev]) => {
        setView(bs.data || null);
        const netRow = (pl.data?.rows || []).find(r => r.code === 'net_result');
        setPlNetResult(netRow || null);
        const prevRow = (plPrev.data?.rows || []).find(r => r.code === 'net_result');
        setPlPrevNet(prevRow ? Number(prevRow.total_ytd || 0) : null);
        setPlLines(plL.data || []);
      })
      .catch(() => addToast(p('Erreur chargement Bilan'), 'error'))
      .finally(() => { if (!silent) setLoading(false); });
  }

  // Ouvre la modale d'ajustement pour une ligne du bilan + un mois (à partir
  // duquel le solde sera décalé).
  function openAdjust(lineId, label, month) {
    setAdjustTarget({ lineId, label, month });
  }

  async function handleCreateAdjust({ amount, counterpart, memo }) {
    const tgt = adjustTarget;
    if (!tgt) return;
    const adjRes = await createAdjustment(activeTenant.id, {
      year, month: tgt.month, description: memo || null,
    });
    if (!adjRes.data) { addToast(adjRes.error || p('Erreur création écriture'), 'error'); return; }
    const plId = counterpart.startsWith('pl:') ? counterpart.slice(3) : '';
    const bsId = counterpart.startsWith('bs:') ? counterpart.slice(3) : '';
    const lineRes = await createAdjustmentLine(activeTenant.id, adjRes.data.id, {
      amount, memo: memo || null,
      bs_line_id_b: tgt.lineId,         // patte « à » = la ligne cliquée (+montant)
      line_id_a: plId || null,        // contrepartie « de » éventuelle (−montant)
      bs_line_id_a: bsId || null,
    });
    if (lineRes.data) { setAdjustTarget(null); reload(true); }
    else addToast(lineRes.error || p('Erreur écriture'), 'error');
  }

  async function handlePaint(account) {
    if (!activeLine) { addToast(p("Choisis d'abord une reporting line (le pinceau).")); return; }
    await handleAssign(account, activeLine === '__unmap__' ? null : activeLine);
  }

  async function handleAssign(account, lineId) {
    const res = await patchBsAccountLine(activeTenant.id, account, lineId || null);
    if (res.data) reload(true);
    else addToast(res.error || p('Erreur affectation'), 'error');
  }

  const lineLabel = id =>
    id === '__unmap__' ? p('Retirer (non mappé)')
      : (lines.find(l => l.id === id)?.label || '—');

  async function handleCreateLine(section, label) {
    const res = await createBsLine(activeTenant.id, section, label);
    if (res.data) reload(true); else addToast(res.error || p('Erreur création ligne'), 'error');
  }
  async function handleRenameLine(id, label) {
    const res = await updateBsLine(activeTenant.id, id, { label });
    if (res.data) reload(true); else addToast(res.error || p('Erreur renommage'), 'error');
  }
  async function handleToggleBfre(id, is_bfre) {
    const res = await updateBsLine(activeTenant.id, id, { is_bfre });
    if (res.data) reload(true); else addToast(res.error || p('Erreur BFRE'), 'error');
  }
  async function handleDeleteLine(id) {
    const res = await deleteBsLine(activeTenant.id, id);
    if (res.data) { if (activeLine === id) setActiveLine(null); reload(true); }
    else addToast(res.error || p('Erreur suppression'), 'error');
  }
  async function handleReorder(ids) {
    const res = await reorderBsLines(activeTenant.id, ids);
    if (res.data) reload(true); else addToast(res.error || p('Erreur réordonnancement'), 'error');
  }
  async function handleRenameSection(code, label) {
    const res = await renameBsSection(activeTenant.id, code, label);
    if (res.data) reload(true); else addToast(res.error || p('Erreur renommage bloc'), 'error');
  }

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeTenant, year, flatForecast, version, cutoff, loadedMonths]);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !activeTenant) return;
    const input = window.prompt(p('Année concernée par ce bilan :'), String(year));
    if (input == null) return;
    const importYear = parseInt(input, 10);
    if (!Number.isFinite(importYear) || importYear < 2000 || importYear > 2100) {
      addToast(t('phrases:p_8fa313abb5'), 'error'); return;
    }
    if (importYear !== year && onYearChange) onYearChange(importYear);
    setImporting(true);
    try {
      const res = await importBsExcel(activeTenant.id, file, importYear);
      if (res.data) {
        const s = res.data;
        addToast(p('Import {{year}} OK : {{accounts}} comptes, mois {{months}}.').replace('{{year}}', importYear).replace('{{accounts}}', s.accounts_imported).replace('{{months}}', (s.months || []).join(', ')));
        reload();
      } else {
        addToast(res.error || p('Erreur import'), 'error');
      }
    } catch (err) {
      addToast(p('Erreur import : {{detail}}').replace('{{detail}}', err.response?.data?.detail || err.message), 'error');
    } finally {
      setImporting(false);
    }
  }

  if (!activeTenant) {
    return (
      <main className="page-table-layout">
        <div className="page-table-empty">{p('Sélectionne une entreprise.')}</div>
      </main>
    );
  }

  // Entête (import + outils) — rendue au PageHeader en page autonome, dans
  // la card en mode Données, téléportée via `toolsHost` sur l'écran Bilan.
  const buildHeadNodes = () => {
    const importAction = readOnly ? null : (
          <>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFile} />
            <button className="p-btn p-btn--primary" onClick={() => fileRef.current?.click()} disabled={importing}>
              {importing ? 'Import…' : p('Importer Excel')}
            </button>
          </>
    );
    const headTools = (<>
        {!readOnly && (
        <button
          className={`p-btn ${unlocked ? 'p-btn--primary' : 'bs-tool'}`}
          title={t('phrases:p_e6af50cfa2')}
          onClick={() => {
            const v = !unlocked;
            setUnlocked(v);
            // Verrouillage : on coupe tous les outils.
            if (!v) { setEditMode(false); setAdjustMode(false); setBrushMode(false); setNotesMode(false); setShowStruct(false); setActiveLine(null); }
          }}
        >
          {unlocked ? '✓ Terminer' : p('✏️ Éditer')}
        </button>
        )}
        {unlocked && (<>
        <button
          className={`p-btn ${editMode ? 'p-btn--primary' : 'bs-tool'}`}
          title={t('phrases:p_5406ad274c')}
          onClick={() => {
            const v = !editMode;
            setEditMode(v);
            if (v) { applyLevel(3); setAdjustMode(false); setBrushMode(false); setNotesMode(false); }
          }}
        >
          {editMode ? p('✓ Édition') : p('✎ Édition')}
        </button>
        <button
          className={`p-btn ${adjustMode ? 'p-btn--primary' : 'bs-tool'}`}
          title={t('phrases:p_4700de4a24')}
          onClick={() => {
            const v = !adjustMode;
            setAdjustMode(v);
            if (v) { setEditMode(false); setBrushMode(false); setNotesMode(false); setActiveLine(null); }
          }}
        >
          {adjustMode ? '✓ Ajuster' : '⊕ Ajuster'}
        </button>
        <button
          className={`p-btn ${brushMode ? 'p-btn--primary' : 'p-btn--brush-off'}`}
          onClick={() => { setBrushMode(v => !v); setActiveLine(null); }}
        >
          {brushMode ? '✓ Brush mode' : '🖌 Brush mode'}
        </button>
        <button
          className={`p-btn ${showStructure ? 'p-btn--secondary' : 'bs-tool'}`}
          onClick={() => setShowStruct(v => !v)}
        >
          {showStructure ? '✓ Structure' : 'Structure'}
        </button>
        </>)}
        {!unitControlled && (
          <button
            className="p-btn bs-tool"
            title={t('phrases:p_15e4e4a269')}
            onClick={() => setThousands(v => !v)}
          >
            {thousands ? 'kCHF' : 'CHF'}
          </button>
        )}
        {/* Niveau de lecture en un seul bouton cyclique : synthétique → ouvert → tout ouvert. */}
        <button
          className="p-btn bs-tool"
          title={t('phrases:p_48a792a833')}
          onClick={() => applyLevel(level >= 3 ? 1 : level + 1)}
        >
          {LEVEL_LABELS[level] || LEVEL_LABELS[1]}
        </button>
        {unlocked && (
        <button
          className={`p-btn ${notesMode ? 'p-btn--primary' : 'bs-tool'}`}
          title={t('phrases:p_545988f9d8')}
          onClick={() => {
            const v = !notesMode;
            setNotesMode(v);
            if (v) { applyLevel(3); setEditMode(false); setAdjustMode(false); setBrushMode(false); setActiveLine(null); }
          }}
        >
          {notesMode ? p('✓ Notes') : p('📝 Notes')}
        </button>
        )}
    </>);
    return { importAction, headTools };
  };

  return (
    <main className={`page-table-layout is-fluid${embedded ? ' is-embedded' : ''}`}>
      {/* Outils d'en-tête : dans le PageHeader en page autonome, ou téléportés
          sur la ligne des modes de l'écran Bilan (prop `toolsHost`) — même
          rangée que les pilules, comme au compte de résultat. */}
      {(() => {
        /* En mode Données (embarqué), l'entête vit dans la CARD (2026-09-01) :
           les boutons flottaient au-dessus de la carte qu'ils commandent. */
        if (embedded && actualsOnly && !toolsHost) return null;
        const { importAction, headTools } = buildHeadNodes();
        if (toolsHost) return createPortal(<>{importAction}{headTools}</>, toolsHost);
        return (
          <PageHeader title={embedded ? '' : p('Bilan')} actions={importAction}>
            {headTools}
          </PageHeader>
        );
      })()}

      {brushMode && (
        <div className="bs-brushbar">
          <span className="bs-brush-hint">
            {activeLine
              ? <>Pinceau : <b>{lineLabel(activeLine)}</b> — clique un compte pour l'affecter.</>
              : <>Clique une <b>reporting line</b> pour charger le pinceau, puis clique les comptes.</>}
          </span>
          <button
            className={`bs-brush-chip${activeLine === '__unmap__' ? ' is-active' : ''} bs-brush-chip--unmap`}
            onClick={() => setActiveLine('__unmap__')}
          >
            Retirer l'affectation
          </button>
          {activeLine && (
            <button className="bs-brush-chip" onClick={() => setActiveLine(null)}>{p('Désélectionner')}</button>
          )}
        </div>
      )}

      {loading && <p className="page-table-loading">{t('common:status.loading')}</p>}

      {!loading && showStructure && (
        <StructureEditor
          sections={sections}
          lines={lines}
          onCreate={handleCreateLine}
          onRename={handleRenameLine}
          onDelete={handleDeleteLine}
          onReorder={handleReorder}
          onRenameSection={handleRenameSection}
          onToggleBfre={handleToggleBfre}
        />
      )}

      {!loading && view && (() => {
        const bsTable = (
          <BsTable
            view={view}
            thousands={thousands}
            plNetResult={plNetResult}
            plPrevNet={plPrevNet}
            unmappedCount={view.unmapped?.length || 0}
            brushMode={brushMode}
            activeLine={activeLine}
            onPickLine={setActiveLine}
            onPaint={handlePaint}
            expanded={expanded}
            onToggleLine={toggleLine}
            expandedSecs={expandedSecs}
            onToggleSection={toggleSection}
            onAssign={handleAssign}
            editMode={editMode}
            onEditValue={handleEditValue}
            onEditForecast={handleEditForecast}
            adjustMode={adjustMode}
            onAdjustCell={openAdjust}
            notesMode={notesMode}
            onEditNote={handleEditNote}
            actualsOnly={actualsOnly}
            monthsFull={monthsFull}
            readOnly={readOnly}
            carded={embedded && actualsOnly}
            cardHead={embedded && actualsOnly ? buildHeadNodes() : null}
            focusSections={aggs.size ? aggregateSections(aggs) : null}
            cardFilters={embedded && actualsOnly ? (
              <PillFilter label={p('Afficher')} value={aggs} onChange={setAggs}
                          options={BS_AGGREGATES.map(({ value, label }) => ({ value, label }))} />
            ) : null}
          />
        );
        // Rapport embarqué (hub Rapports → Bilan) : la Card enveloppe la
        // table principale À L'INTÉRIEUR de BsTable ; le pied de contrôle sort sous
        // la card. Hors hub (standalone), table nue via son propre page-table-wrap.
        return bsTable;
      })()}

      {adjustTarget && (
        <AdjustModal
          target={adjustTarget}
          year={year}
          thousands={thousands}
          plLines={plLines}
          bsLines={lines}
          onClose={() => setAdjustTarget(null)}
          onSubmit={handleCreateAdjust}
        />
      )}
    </main>
  );
}

// ── Éditeur de structure ──────────────────────────────────────────────────────
function StructureEditor({ sections, lines, onCreate, onRename, onDelete, onReorder, onRenameSection, onToggleBfre }) {
  return (
    <div className="bs-structwrap">
      <div className="bs-map-head">{p('Structure du Bilan — lignes par bloc')}</div>
      <div className="bs-struct-body">
        {sections.map(sec => {
          const secLines = lines.filter(l => l.section === sec.code);
          const ids = secLines.map(l => l.id);
          const move = (i, dir) => {
            const j = i + dir;
            if (j < 0 || j >= ids.length) return;
            const next = ids.slice();
            [next[i], next[j]] = [next[j], next[i]];
            onReorder(next);
          };
          return (
            <div key={sec.code} className="bs-struct-sec">
              <input
                className="bs-struct-sec-h bs-struct-sec-input"
                defaultValue={sec.label}
                title={p('Renommer le bloc')}
                onBlur={e => { const v = e.target.value.trim(); if (v && v !== sec.label) onRenameSection(sec.code, v); }}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
              />
              {secLines.map((l, i) => (
                <div key={l.id} className="bs-struct-line">
                  <input
                    className="bs-struct-input"
                    defaultValue={l.label}
                    onBlur={e => { const v = e.target.value.trim(); if (v && v !== l.label) onRename(l.id, v); }}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                  />
                  <button
                    className={`bs-struct-bfre${l.is_bfre ? ' is-on' : ''}`}
                    title={l.is_bfre ? p('Retirer du BFRE') : p('Marquer comme BFRE (besoin en fonds de roulement)')}
                    onClick={() => onToggleBfre(l.id, !l.is_bfre)}
                  >BFRE</button>
                  <button className="bs-struct-ic" title={p("Monter")} disabled={i === 0} onClick={() => move(i, -1)}>▲</button>
                  <button className="bs-struct-ic" title={p("Descendre")} disabled={i === secLines.length - 1} onClick={() => move(i, 1)}>▼</button>
                  <button className="bs-struct-ic bs-struct-del" title={p("Supprimer")}
                          onClick={() => { if (window.confirm(p('Supprimer « {{label}} » ?').replace('{{label}}', l.label))) onDelete(l.id); }}>×</button>
                </div>
              ))}
              <AddLine onAdd={label => onCreate(sec.code, label)} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AddLine({ onAdd }) {
  const { t } = useTranslation();
  const p = useP();
  const [val, setVal] = useState('');
  function submit() { const v = val.trim(); if (v) { onAdd(v); setVal(''); } }
  return (
    <div className="bs-struct-add">
      <input
        className="bs-struct-input"
        placeholder={p('+ Ajouter une ligne…')}
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') submit(); }}
      />
      <button className="bs-struct-addbtn" onClick={submit} disabled={!val.trim()}>{t('common:actions.add')}</button>
    </div>
  );
}

// ── Cellule numérique éditable ────────────────────────────────────────────────
function InlineNum({ value, onSave, forecast = false, thousands = true }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const ref = useRef();
  useEffect(() => { if (editing && ref.current) { ref.current.focus(); ref.current.select(); } }, [editing]);
  function start(e) { e.stopPropagation(); setDraft(value ? String(value) : ''); setEditing(true); }
  function commit() {
    setEditing(false);
    const v = parseFloat(String(draft).trim().replace(/[\s']/g, '').replace(',', '.')) || 0;
    if (v !== value) onSave(v);
  }
  if (editing) {
    return (
      <input
        ref={ref}
        className="bs-edit-input"
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') setEditing(false);
        }}
      />
    );
  }
  return (
    <span
      className={`bs-edit-cell${forecast ? ' bs-edit-cell--forecast' : ''}`}
      onClick={start}
      title={forecast ? p('Forecast — cliquer pour saisir') : p('Cliquer pour modifier')}
    >{chf(value, thousands)}</span>
  );
}

// ── Table principale ──────────────────────────────────────────────────────────
export function BsTable({ view, thousands, plNetResult, plPrevNet = null, unmappedCount, brushMode, activeLine, onPickLine, onPaint, expanded, onToggleLine,
                   expandedSecs, onToggleSection, onAssign, editMode, onEditValue, onEditForecast,
                   adjustMode, onAdjustCell, notesMode, onEditNote, actualsOnly, readOnly = false, monthsFull, carded, cardHead = null,
                   lead2 = null, focusSections = null, cardFilters = null }) {
  const { t } = useTranslation();
  const p = useP();
const fmt = v => chf(v, thousands);
  const closedCount = Number.isFinite(view.closed_count) ? view.closed_count : 12;
  const isForecast = i => (i + 1) > closedCount;
  const hasReal = closedCount > 0;
  // Réel seul (États financiers) : on borne l'affichage aux mois clôturés.
  // `monthsFull` (impression) force les 12 mois (mise en page stable, vides = «–»).
  const visCount = (actualsOnly && !monthsFull) ? Math.max(closedCount, 0) : 12;
  const monthLabels = MONTHS.slice(0, visCount);
  const months = m => monthLabels.map((_, i) => Number(m[String(i + 1)] || 0));
  /* Une masse retenue par les raccourcis s'ouvre en entier ; les autres
     disparaissent (2026-10-03). */
  const focus     = focusSections && focusSections.size ? focusSections : null;
  const isOpen    = code => (focus ? true : expanded.has(code));
  const isSecOpen = code => (focus ? focus.has(code) : expandedSecs.has(code));
  const secHasLines = new Set(view.rows.filter(r => r.type === 'detail').map(r => r.section));
  const [dragAcc, setDragAcc]   = useState(null);
  const [overCode, setOverCode] = useState(null);

  const prevYear = view.prev_year ?? (view.year - 1);
  const nCls = (v, i) => `bs-num${isForecast(i) ? ' bs-mforecast' : ''}`;

  // Colonne de tête (réel seul) : clôture N-1 = point de départ (31.12 N-1),
  // suivie d'une colonne séparatrice 14 px qui isole l'ancre du bloc des mois.
  // `lead2` (impression) = { year, byKey } : colonne 31.12.N-2 supplémentaire,
  // valeurs par clé de ligne (code / acc:numéro), avant la clôture N-1.
  const lead2Cell = (key) => (actualsOnly && lead2
    ? <td className="bs-num bs-num--prev bs-lead-prev">{lead2.byKey[key] == null ? '–' : fmt(lead2.byKey[key])}</td>
    : null);
  const leadDec = (prevDec, key) => (actualsOnly
    ? <>{lead2Cell(key)}<td className="bs-num bs-num--prev bs-lead-prev">{fmt(prevDec)}</td><td className="bs-spacer" /></>
    : null);
  const leadEmpty = actualsOnly
    ? <>{lead2 && <td className="bs-num bs-lead-prev" />}<td className="bs-num bs-lead-prev" /><td className="bs-spacer" /></>
    : null;

  // Gouttière fixe de 20 px en tête de libellé : chevron (rubrique), icône
  // (équilibre/mémo) ou vide (détail) → tous les textes démarrent au même x.
  const Gut = ({ children }) => <span className="bs-gut">{children || ''}</span>;
  // Nombre total de colonnes (réel seul) : libellé + ancre + séparateur + mois + variation.
  const totalCols = 1 + (actualsOnly ? 2 + (lead2 ? 1 : 0) : 0) + visCount + (actualsOnly ? 1 : 2);

  // Bloc comparatif de fin : clôture N-1 + variation (mode complet), ou variation
  // seule (réel seul — le 31.12 N-1 est passé en tête de tableau).
  function cmpCells(prevDec, real) {
    const variation = hasReal ? Number(real || 0) - Number(prevDec || 0) : 0;
    const varCls = variation > 0 ? ' is-pos' : variation < 0 ? ' is-neg' : '';
    const varCell = <td className={`bs-num bs-num--var bs-cmp-start${varCls}`}>{hasReal ? fmt(variation) : '–'}</td>;
    if (actualsOnly) return varCell;
    return (
      <>
        <td className="bs-num bs-num--prev bs-cmp-start">{fmt(prevDec)}</td>
        <td className={`bs-num bs-num--var${varCls}`}>{hasReal ? fmt(variation) : '–'}</td>
      </>
    );
  }
  // Cellules vides du bloc comparatif (lignes sans comparatif pertinent).
  const cmpEmpty = actualsOnly ? (
    <td className="bs-num bs-cmp-start" />
  ) : (
    <>
      <td className="bs-num bs-cmp-start" />
      <td className="bs-num" />
    </>
  );

  function dropProps(targetCode, lineIdOrNull) {
    return {
      onDragOver: e => { if (dragAcc) { e.preventDefault(); setOverCode(targetCode); } },
      onDragLeave: () => setOverCode(c => c === targetCode ? null : c),
      onDrop: e => {
        e.preventDefault();
        if (dragAcc) onAssign(dragAcc, lineIdOrNull);
        setDragAcc(null); setOverCode(null);
      },
    };
  }

  function lineRow(row) {
    const hasAccts = row.accounts.length > 0;
    const adjs     = row.adjustments || [];
    const hasDetail = hasAccts || adjs.length > 0;
    const open     = isOpen(row.code);
    const active   = brushMode && activeLine === row.code;
    const rowClick = adjustMode
      ? undefined
      : (brushMode
          ? () => onPickLine(row.code)
          : (hasDetail ? () => onToggleLine(row.code) : undefined));
    return (
      <tr
        className={`bs-row bs-row--line${brushMode ? ' bs-pickable' : ''}${active ? ' bs-brush-active' : ''}${hasDetail && !brushMode && !adjustMode ? ' bs-collapsible' : ''}${overCode === row.code ? ' bs-droptarget' : ''}`}
        onClick={rowClick}
        {...dropProps(row.code, row.code)}
      >
        <td className="bs-line">
          {actualsOnly
            ? <Gut>{hasDetail ? <IconChevron open={open} /> : null}</Gut>
            : <span className="bs-caret" onClick={hasDetail ? e => { e.stopPropagation(); onToggleLine(row.code); } : undefined}>
                {hasDetail ? (open ? '▾' : '▸') : ''}
              </span>}
          {active ? '🖌 ' : ''}{row.label}
          {adjs.length > 0 && (
            <span className="bs-adj-flag" title={`${adjs.length} ajustement(s)`}>⊕{adjs.length}</span>
          )}
        </td>
        {leadDec(row.prev_dec, row.code)}
        {months(row.months).map((v, i) => (
          <td key={i} className={nCls(v, i)}>
            {isForecast(i) && !readOnly
              ? <InlineNum value={v} thousands={thousands} forecast onSave={nv => onEditForecast(row.code, i + 1, nv)} />
              : (adjustMode
                  ? <span className="bs-adjustable" title={t('phrases:p_f9cb1f2421')}
                          onClick={e => { e.stopPropagation(); onAdjustCell(row.code, row.label, i + 1); }}>{fmt(v)}</span>
                  : fmt(v))}
          </td>
        ))}
        {cmpCells(row.prev_dec, row.total_ytd)}
      </tr>
    );
  }

  function adjRow(adj, lineCode) {
    return (
      <tr key={`${lineCode}-adj-${adj.adj_line_id}`} className="bs-row bs-row--adj">
        <td className="bs-line bs-adj-cell">
          <span className="bs-adj-ref">⊕ {adj.entry_ref}</span>
          <span className="bs-adj-desc">{adj.description || adj.memo || 'Ajustement'}</span>
        </td>
        {leadEmpty}
        {/* UN AJUSTEMENT DE BILAN EST UN STOCK (correctif 2026-09-24, repéré le
            18.07) : il vaut de son mois de saisie jusqu'à décembre, et le
            serveur le reporte ainsi sur la ligne parent. Le détail déplié ne
            le montrait QUE dans la colonne de saisie, si bien que de M+1 à
            décembre le total du poste ne valait plus la somme de ses lignes :
            le chiffre était juste, le détail mentait.
            ⚠️ La condition identique de `ProfitLossPage` reste un `===` : un
            ajustement de résultat est un FLUX, il appartient à son seul mois. */}
        {monthLabels.map((_, i) => (
          <td key={i} className={nCls(0, i)}>
            {i + 1 >= adj.month ? fmt(adj.amount) : ''}
          </td>
        ))}
        {cmpEmpty}
      </tr>
    );
  }

  function accRow(a, extraCls = '') {
    const drag = !readOnly && !editMode && !notesMode;
    const notes = a.notes || {};
    return (
      <tr
        key={a.account_number}
        className={`bs-row bs-row--acc${drag ? ' bs-draggable' : ''}${extraCls}${brushMode && !editMode ? ' bs-paintable' : ''}${dragAcc === a.account_number ? ' bs-dragging' : ''}`}
        draggable={drag}
        onDragStart={drag ? e => { setDragAcc(a.account_number); e.dataTransfer.effectAllowed = 'move'; } : undefined}
        onDragEnd={drag ? () => { setDragAcc(null); setOverCode(null); } : undefined}
        onClick={brushMode && !editMode ? () => onPaint(a.account_number) : undefined}
      >
        <td className="bs-line bs-acc-cell" title={`${a.account_number} · ${a.account_desc}`}>
          {actualsOnly && <Gut />}
          <span className="bs-mono">{a.account_number}</span>
          <span className="bs-acc-desc">{a.account_desc}</span>
        </td>
        {leadDec(a.prev_dec, `acc:${a.account_number}`)}
        {months(a.months).map((v, i) => {
          const m = i + 1;
          const note = notes[String(m)];
          const body = isForecast(i) ? <span className="bs-td-muted">–</span> : fmt(v);
          return (
            <td key={i} className={`${nCls(v, i)}${note ? ' bs-has-note' : ''}`}>
              {editMode && !readOnly && !isForecast(i) ? (
                <InlineNum value={v} thousands={thousands} onSave={nv => onEditValue(a.account_number, m, nv)} />
              ) : notesMode ? (
                <span className="bs-noteable"
                      onClick={e => { e.stopPropagation(); onEditNote(a.account_number, m, note || ''); }}>
                  {body}{note ? <span className="bs-note-dot" /> : null}
                </span>
              ) : (
                <>{body}{note ? (
                  <span className="bs-note-mark" tabIndex={0}>
                    <span className="bs-note-dot" />
                    <span className="bs-note-tip" role="tooltip">{note}</span>
                  </span>
                ) : null}</>
              )}
            </td>
          );
        })}
        {cmpCells(a.prev_dec, a.total_ytd)}
      </tr>
    );
  }

  /* Colonnes IDENTIQUES entre la card et le pied de contrôle (2026-08-17) :
     deux tables séparées ne s'alignent qu'avec le même gabarit de colonnes. */
  const ColsActuals = () => (actualsOnly ? (
    <colgroup>
      <col className="bs-c-line" />
      {lead2 && <col className="bs-c-lead" />}
      <col className="bs-c-lead" />
      <col className="bs-c-sp" />
      {monthLabels.map((mo) => <col key={mo} className="bs-c-mo" />)}
      <col className="bs-c-var" />
    </colgroup>
  ) : null);

  const mainWrap = (
    <div className="page-table-wrap">
      <table className={`page-table bs-table${actualsOnly ? ' bs-actuals' : ''}`}>
        <ColsActuals />
        <thead>
          {actualsOnly ? (
            <>
              <tr className="bs-grouphead">
                <th className="bs-col-line" />
                <th className="bs-grp-close" colSpan={lead2 ? 2 : 1} />
                <th className="bs-spacer" />
                <th className="bs-grp-months" colSpan={visCount} />
                <th className="bs-grp-var">Var.</th>
              </tr>
              <tr className="bs-colhead">
                <th className="bs-col-line">{p('N° compte · désignation')}</th>
                {lead2 && <th className="bs-col-num bs-lead-prev">{p('Déc.')} {lead2.year}</th>}
                <th className="bs-col-num bs-lead-prev">{p('Déc.')} {prevYear}</th>
                <th className="bs-spacer" />
                {monthLabels.map(mo => <th key={mo} className="bs-col-num">{mo}</th>)}
                <th className="bs-col-num bs-col-var">Δ vs {prevYear}</th>
              </tr>
            </>
          ) : (
            <>
              {/* Bandeau standard RÉALISÉ / PRÉVISIONNEL (2026-08-20) — il
                  remplace l'ex-sous-rangée Actual/Forecast par colonne. */}
              <tr className="bs-band-row">
                <th className="bs-col-line" />
                {closedCount > 0 && (
                  <th className="lk-band lk-band--real" colSpan={closedCount}>{t('finance:reports.bandReal')}</th>
                )}
                {closedCount < 12 && (
                  <th className="lk-band lk-band--fore" colSpan={12 - closedCount}>{t('finance:reports.bandFore')}</th>
                )}
                <th className="bs-cmp-start" colSpan={2} />
              </tr>
              <tr>
                <th className="bs-col-line">{t('phrases:p_0b70cdfff9')}</th>
                {monthLabels.map(mo => <th key={mo} className="bs-col-num">{mo}</th>)}
                <th className="bs-col-num bs-cmp-start bs-col-prev">31.12.{prevYear}</th>
                <th className="bs-col-num bs-cmp-start bs-col-var">Variation</th>
              </tr>
            </>
          )}
        </thead>
        <tbody>
          {view.rows.map(row => {
            if (row.type === 'subtotal') {
              const isGrand    = row.code === 'total_assets' || row.code === 'total_liabilities_equity';
              // Filtré par masse, un total de l'actif ou du passif ne somme
              // plus les lignes à l'écran : il se tait.
              if (focus && isGrand) return null;
              const isCheck    = row.code === 'balance_check';
              if (isCheck && actualsOnly) return null;   // rendu dans le pied détaché
              const rowMonths  = months(row.months);
              // L'équilibre doit tenir CHAQUE mois réel — pas seulement le total
              // (qui ne reflète que décembre, souvent vide en cours d'année).
              const worstDiff  = isCheck
                ? rowMonths.reduce((mx, v, i) => isForecast(i) ? mx : Math.max(mx, Math.abs(v)), 0)
                : 0;
              const balanced   = isCheck && worstDiff < 1;
              const hasUnmapped = isCheck && unmappedCount > 0;

              let rowCls = 'bs-row bs-row--subtotal';
              if (isGrand)                   rowCls += ' bs-row--grand';
              if (isCheck && hasUnmapped)    rowCls = 'bs-row bs-row--check-warn';
              else if (isCheck && balanced)  rowCls = 'bs-row bs-row--balanced';
              else if (isCheck && !balanced) rowCls = 'bs-row bs-row--unbalanced';

              const balanceLabel = isCheck
                ? (hasUnmapped
                    ? `⚠ ${row.label} — ${unmappedCount} ${p('compte(s) non mappé(s)')}`
                    : (balanced ? `✓ ${row.label} — OK` : `⚠ ${row.label} — ${p('écart !')}`))
                : row.label;

              return (
                <tr key={row.code} className={rowCls}>
                  <td className="bs-line">{balanceLabel}</td>
                  {leadDec(row.prev_dec, row.code)}
                  {rowMonths.map((v, i) => <td key={i} className={nCls(v, i)}>{fmt(v)}</td>)}
                  {cmpCells(row.prev_dec, row.total_ytd)}
                          </tr>
              );
            }
            if (row.type === 'section_total') {
              if (focus && !focus.has(row.section)) return null;
              const has  = !focus && secHasLines.has(row.section);
              const open = isSecOpen(row.section);
              /* AGRÉGÉ, PAS DE FILET ENTRE LES MASSES (2026-10-03) : une masse
                 fermée ne porte plus le filet noir qui la séparait de la
                 suivante ; ouverte, il sépare son en-tête de ses lignes. */
              return (
                <tr
                  key={row.code}
                  className={`bs-row bs-row--sectiontotal${has ? ' bs-collapsible' : ''}${open ? '' : ' bs-row--sec-closed'}`}
                  onClick={has ? () => onToggleSection(row.section) : undefined}
                >
                  <td className="bs-line">
                    {actualsOnly
                      ? <Gut>{has ? <IconChevron open={open} /> : null}</Gut>
                      : <span className="bs-caret">{has ? (open ? '▾' : '▸') : ''}</span>}
                    {row.label}
                  </td>
                  {leadDec(row.prev_dec, row.code)}
                  {months(row.months).map((v, i) => <td key={i} className={nCls(v, i)}>{fmt(v)}</td>)}
                  {cmpCells(row.prev_dec, row.total_ytd)}
                          </tr>
              );
            }
            if (!isSecOpen(row.section)) return null;
            return (
              <React.Fragment key={row.code}>
                {lineRow(row)}
                {isOpen(row.code) && row.accounts.map(a => accRow(a))}
                {isOpen(row.code) && (row.adjustments || []).map(adj => adjRow(adj, row.code))}
              </React.Fragment>
            );
          })}

          {/* Comptes non alloués AVEC comptes = affordance de mapping (drop) →
             reste dans le corps du tableau. Le résumé « aucun » et les contrôles
             d'équilibre partent dans le pied détaché sous la card (voir footerTbl). */}
          {view.unmapped.length > 0 && !focus && (
            <>
              <tr
                className={`bs-row bs-row--line bs-row--unmapped${overCode === 'unmapped' ? ' bs-droptarget' : ''}`}
                {...dropProps('unmapped', null)}
              >
                <td className="bs-line">
                  {actualsOnly ? p('Comptes non alloués') : `${p('Non mappé — ')}${brushMode ? p('clique un compte') : p('glisse un compte ici')}`}
                </td>
                {leadDec(view.unmapped.reduce((s, a) => s + Number(a.prev_dec || 0), 0), '__unmapped')}
                {monthLabels.map((_, i) => (
                  <td key={i} className={nCls(0, i)}>
                    {fmt(view.unmapped.reduce((s, a) => s + Number(a.months[String(i + 1)] || 0), 0))}
                  </td>
                ))}
                {cmpCells(
                  view.unmapped.reduce((s, a) => s + Number(a.prev_dec || 0), 0),
                  view.unmapped.reduce((s, a) => s + Number(a.total_ytd || 0), 0),
                )}
              </tr>
              {view.unmapped.map(a => accRow(a, ' bs-row--unmapped'))}
            </>
          )}
        </tbody>
      </table>

      {!actualsOnly && plNetResult && (
        <table className="page-table bs-table bs-recon-table">
          <tbody>
            <tr className="bs-row bs-row--recon">
              <td className="bs-line bs-col-line">↳ {plNetResult.label}</td>
              {monthLabels.map((_, i) => (
                <td key={i} className={`bs-num${isForecast(i) ? ' bs-mforecast' : ''}`}>
                  {fmt(Number(plNetResult.months[String(i + 1)] || 0))}
                </td>
              ))}
              {cmpEmpty}
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );

  // Pied de contrôle (réel seul) : comptes non alloués (résumé), équilibre et
  // mémo résultat net cumulé. Sorti en table séparée SOUS la card — ce sont des
  // contrôles, pas des lignes de l'état financier.
  let footerTbl = null;
  if (actualsOnly) {
    const chk = view.rows.find(r => r.code === 'balance_check');
    const chkMonths = chk ? months(chk.months) : [];
    const worst = chkMonths.reduce((mx, v, i) => isForecast(i) ? mx : Math.max(mx, Math.abs(v)), 0);
    const balanced = worst < 1;
    const ok = balanced && unmappedCount === 0;
    const equLabel = unmappedCount > 0
      ? `${p('Équilibre — ')}${unmappedCount} ${p('compte(s) non mappé(s)')}`
      : (balanced ? p('Équilibre — OK') : p('Équilibre — écart'));
    /* UN RAPPORT DIT SON VERDICT, PAS SON CONTRÔLE (2026-09-18). Le pied
       déroulait l'écart mois par mois et le résultat cumulé de l'exercice :
       c'est ce qu'on regarde quand on VÉRIFIE une saisie, et cela se vérifie
       dans Données comptables. Sur l'état qui se lit, il ne reste que la
       réponse — équilibré, ou l'écart nommé en rouge. */
    if (readOnly) {
      footerTbl = chk ? (
        <div className={`bs-verdict${ok ? ' is-ok' : ' is-ko'}`}>
          {ok ? <IconCheck /> : <IconAlert />}
          <span>{ok ? p('Équilibré') : equLabel}</span>
        </div>
      ) : null;
    } else {
      /* CONTRÔLES DU BILAN (2026-10-16, proto A) : trois lignes calées sur les
         mois de la card, une icône d'état et l'écart du mois. Zéro s'écrit « – ». */
      const resRow = view.rows.find(r => r.std_key === 'current_result');
      const nonAlloue = monthLabels.map((_, i) => view.unmapped.reduce(
        (sum, a) => sum + Math.abs(Number(a.months[String(i + 1)] || 0)), 0));
      let cumPl = 0;
      const ecartResultat = plNetResult && resRow ? monthLabels.map((_, i) => {
        cumPl += Number(plNetResult.months[String(i + 1)] || 0);
        return Number(resRow.months[String(i + 1)] || 0) - cumPl;
      }) : null;
      const controles = [
        { key: 'unmapped', label: p('Comptes non alloués'), vals: nonAlloue,
          dec: view.unmapped.reduce((sum, a) => sum + Math.abs(Number(a.prev_dec || 0)), 0) },
        chk && { key: 'equ', label: p('Équilibre actif − passif'), vals: chkMonths, dec: Number(chk.prev_dec || 0) },
        ecartResultat && { key: 'res', label: p('Résultat du bilan − compte de résultat'), vals: ecartResultat,
          dec: plPrevNet != null && resRow ? Number(resRow.prev_dec || 0) - plPrevNet : null },
      ].filter(Boolean);
      footerTbl = (
        <div className="page-table-wrap bs-foot-wrap">
          <table className="page-table bs-table bs-actuals bs-foot-table">
            <ColsActuals />
            <tbody>
              <tr className="bs-row bs-foot-periods">
                <td className="bs-line" />
                <td className="bs-num bs-lead-prev">{p('Déc.')} {prevYear}</td>
                <td className="bs-spacer" />
                {monthLabels.map(mo => <td key={mo} className="bs-num">{mo}</td>)}
                <td className="bs-num bs-cmp-start" />
              </tr>
              {controles.map(c => {
                const decOk = c.dec == null || Math.abs(c.dec) < 1;
                const juste = decOk && c.vals.every((v, i) => isForecast(i) || Math.abs(v) < 1);
                return (
                  <tr key={c.key} className={`bs-row bs-row--ctl${juste ? ' is-ok' : ' is-ko'}`}>
                    <td className="bs-line"><Gut>{juste ? <IconCheck /> : <IconAlert />}</Gut>{c.label}</td>
                    {lead2 && <td className="bs-num bs-lead-prev" />}
                    <td className={`bs-num bs-lead-prev${decOk ? '' : ' bs-ctl-ecart'}`}>{decOk ? '–' : fmt(c.dec)}</td>
                    <td className="bs-spacer" />
                    {c.vals.map((v, i) => (
                      <td key={i} className={`bs-num${!isForecast(i) && Math.abs(v) >= 1 ? ' bs-ctl-ecart' : ''}`}>
                        {isForecast(i) || Math.abs(v) < 1 ? '–' : fmt(v)}
                      </td>
                    ))}
                    <td className="bs-num" />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
  }

  /* Mode Données : la card porte l'entête standard, import et outils compris
     (2026-09-01) — ils flottaient au-dessus de la carte qu'ils commandent. */
  return carded
    ? <><CardShell className="report-card" title={p('Bilan')}
          viewControls={cardHead ? <>{cardHead.importAction}{cardHead.headTools}</> : null}
          filters={cardFilters} filterCount={focus ? 1 : 0}>
          {mainWrap}
        </CardShell>{footerTbl}</>
    : <>{mainWrap}{footerTbl}</>;
}

// ── Modale d'ajustement (clic sur une valeur du bilan) ────────────────────────
function AdjustModal({ target, year, thousands, plLines, bsLines, onClose, onSubmit }) {
  const { t } = useTranslation();
  const p = useP();
const [amount, setAmount]         = useState('');
  const [counterpart, setCounter]   = useState('');
  const [memo, setMemo]             = useState('');
  const monthLabel = `${MONTHS[target.month - 1]} ${year}`;

  function submit() {
    const v = parseFloat(String(amount).replace(/[\s']/g, '').replace(',', '.'));
    if (!Number.isFinite(v) || v === 0) return;
    onSubmit({ amount: v, counterpart, memo: memo.trim() });
  }

  const grouped = ls => {
    const g = {};
    for (const l of ls) { const s = l.section_label || l.section; (g[s] = g[s] || []).push(l); }
    return g;
  };
  const plG = grouped(plLines);
  const bsG = grouped(bsLines);

  return (
    <div className="bs-modal-overlay">
      <div className="bs-modal" onClick={e => e.stopPropagation()}>
        <div className="bs-modal-head">Ajuster — {target.label}</div>
        <div className="bs-modal-body">
          <label className="bs-modal-field">
            <span>{p('À partir de')}</span>
            <input className="bs-modal-input" value={monthLabel} readOnly />
          </label>
          <label className="bs-modal-field">
            <span>Montant (CHF)</span>
            <input
              className="bs-modal-input"
              type="text" inputMode="decimal" autoFocus
              placeholder={t('phrases:p_5770233fd5')}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onClose(); }}
            />
          </label>
          <label className="bs-modal-field">
            <span>{p('Contrepartie')}</span>
            <select className="bs-modal-input" value={counterpart} onChange={e => setCounter(e.target.value)}>
              <option value="">{p('— aucune (écriture à une patte, déséquilibre voulu) —')}</option>
              {Object.entries(plG).map(([sec, ls]) => (
                <optgroup key={`pl-${sec}`} label={t('phrases:p_4cbfd12321', { sec })}>
                  {ls.map(l => <option key={l.id} value={`pl:${l.id}`}>{l.label}</option>)}
                </optgroup>
              ))}
              {Object.entries(bsG).map(([sec, ls]) => (
                <optgroup key={`bs-${sec}`} label={t('phrases:p_6ad0a93de1', { sec })}>
                  {ls.map(l => <option key={l.id} value={`bs:${l.id}`}>{l.label}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
          <label className="bs-modal-field">
            <span>{t('phrases:p_f5485bbae8')}</span>
            <input
              className="bs-modal-input"
              placeholder={t('phrases:p_bc57aa2e32')}
              value={memo}
              onChange={e => setMemo(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submit(); }}
            />
          </label>
        </div>
        <div className="bs-modal-foot">
          <button className="p-btn bs-tool" onClick={onClose}>{t('common:actions.cancel')}</button>
          <button className="p-btn p-btn--primary" onClick={submit}>{p("Passer l'écriture")}</button>
        </div>
      </div>
    </div>
  );
}
